import { supabase } from '../lib/supabase'
import { getCurrentUserId, verifyPropertyAccess, verifyPropertyWriteAccess } from '../lib/auth'
import { validateFile, sanitizeFilename, complianceDocumentInsertSchema, verificationConfirmationSchema } from '../lib/validators'
import { getSignedUrl, getSignedUrls } from '../lib/storage'
import { applyPropertyFilter } from '../lib/portfolioFilter'
import type { ComplianceDocument, ComplianceDocumentType } from '../types/compliance'
import {
  logComplianceDocumentUploaded,
  logComplianceDocumentUpdated,
  logComplianceDocumentDeleted,
  logComplianceDocumentArchived,
} from '../lib/audit'
import type { VerificationConfirmation } from '../components/compliance/DocumentVerificationModal'

export interface ComplianceDocumentInsert {
  property_id: string
  document_type: ComplianceDocumentType
  document_name?: string | null
  issue_date?: string | null
  expiry_date?: string | null
  notes?: string | null
}

export async function uploadComplianceDocument(
  input: ComplianceDocumentInsert,
  file?: File,
  verification?: VerificationConfirmation,
): Promise<ComplianceDocument> {
  // Validate inputs
  complianceDocumentInsertSchema.parse(input)
  if (verification) verificationConfirmationSchema.parse(verification)

  const userId = await getCurrentUserId()
  let storagePath: string | null = null
  let originalName: string | null = null
  let fileSize: number | null = null

  if (file) {
    // Validate file before upload
    const validation = validateFile(file, { isDocument: true })
    if (!validation.valid) {
      throw new Error(validation.error)
    }

    const sanitizedName = sanitizeFilename(file.name)
    const ext = sanitizedName.split('.').pop() || 'pdf'
    storagePath = `${input.property_id}/${input.document_type}/${Date.now()}-${crypto.randomUUID().slice(0, 12)}.${ext}`

    const { error: uploadError } = await supabase.storage
      .from('compliance-documents')
      .upload(storagePath, file, { contentType: file.type })

    if (uploadError) throw uploadError
    originalName = sanitizedName
    fileSize = file.size
  }

  const { data, error } = await supabase
    .from('compliance_documents')
    .insert({
      property_id: input.property_id,
      document_type: input.document_type,
      document_name: input.document_name || null,
      storage_path: storagePath,
      original_name: originalName,
      file_size: fileSize,
      issue_date: input.issue_date || null,
      expiry_date: input.expiry_date || null,
      notes: input.notes || null,
      uploaded_by: userId,
    })
    .select()
    .single()

  if (error) throw error

  // Audit log — includes verification metadata when available
  const auditMetadata: Record<string, unknown> = {
    document_type: input.document_type,
    file_name: originalName,
    file_size: fileSize,
    mime_type: file?.type || null,
    property_id: input.property_id,
  }
  if (verification) {
    auditMetadata.detection_confidence = verification.detection.confidenceLevel
    auditMetadata.detected_keywords = verification.detection.detectedKeywords
    auditMetadata.matched_patterns = verification.detection.matchedPatterns
    auditMetadata.detection_warnings = verification.detection.warnings
    auditMetadata.warning_shown = true
    auditMetadata.warning_level = verification.warningLevel
    auditMetadata.checklist_items = verification.checklistItems
    auditMetadata.confirmed_at = verification.confirmedAt
    auditMetadata.confirmed_by = userId
  }
  await logComplianceDocumentUploaded(data.id, auditMetadata)

  return await addDocumentUrl(data as ComplianceDocument)
}

export async function updateComplianceDocument(
  id: string,
  input: Partial<ComplianceDocumentInsert>,
  file?: File
): Promise<ComplianceDocument> {
  // Verify ownership via the document's property
  const { data: doc } = await supabase.from('compliance_documents').select('property_id').eq('id', id).is('deleted_at', null).single()
  if (!doc) throw new Error('Compliance document not found')
  await verifyPropertyWriteAccess(doc.property_id)

  const updates: Record<string, unknown> = {}

  if (input.document_type !== undefined) updates.document_type = input.document_type
  if (input.document_name !== undefined) updates.document_name = input.document_name
  if (input.issue_date !== undefined) updates.issue_date = input.issue_date
  if (input.expiry_date !== undefined) updates.expiry_date = input.expiry_date
  if (input.notes !== undefined) updates.notes = input.notes

  if (file) {
    // Validate file before upload
    const validation = validateFile(file, { isDocument: true })
    if (!validation.valid) {
      throw new Error(validation.error)
    }

    // Get existing document to find property_id
    const { data: existing } = await supabase
      .from('compliance_documents')
      .select('property_id, storage_path')
      .eq('id', id)
      .is('deleted_at', null)
      .single()

    if (existing) {
      // Delete old file if exists
      if (existing.storage_path) {
        await supabase.storage.from('compliance-documents').remove([existing.storage_path])
      }

      const sanitizedName = sanitizeFilename(file.name)
      const ext = sanitizedName.split('.').pop() || 'pdf'
      const storagePath = `${existing.property_id}/${input.document_type || 'other'}/${Date.now()}-${crypto.randomUUID().slice(0, 12)}.${ext}`

      const { error: uploadError } = await supabase.storage
        .from('compliance-documents')
        .upload(storagePath, file, { contentType: file.type })

      if (uploadError) throw uploadError

      updates.storage_path = storagePath
      updates.original_name = sanitizedName
      updates.file_size = file.size
    }
  }

  const { data, error } = await supabase
    .from('compliance_documents')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error

  await logComplianceDocumentUpdated(
    id,
    { document_type: doc.property_id },
    { ...updates, property_id: doc.property_id },
  )

  return await addDocumentUrl(data as ComplianceDocument)
}

export async function deleteComplianceDocument(id: string): Promise<void> {
  // Get document to verify ownership (storage file preserved for audit trail)
  const { data: doc } = await supabase
    .from('compliance_documents')
    .select('storage_path, property_id')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (!doc) throw new Error('Compliance document not found')
  await verifyPropertyWriteAccess(doc.property_id)

  // Soft-delete — storage file intentionally preserved for compliance audit trail
  const { error } = await supabase.rpc('soft_delete_record', {
    target_table: 'compliance_documents',
    record_id: id,
  })

  if (error) throw error

  await logComplianceDocumentDeleted(id, {
    property_id: doc.property_id,
    storage_path: doc.storage_path,
  })
}

export async function archiveComplianceDocument(id: string): Promise<void> {
  // Verify ownership via the document's property
  const { data: doc } = await supabase.from('compliance_documents').select('property_id').eq('id', id).is('deleted_at', null).single()
  if (!doc) throw new Error('Compliance document not found')
  await verifyPropertyWriteAccess(doc.property_id)

  const { error } = await supabase
    .from('compliance_documents')
    .update({ is_current: false })
    .eq('id', id)

  if (error) throw error

  await logComplianceDocumentArchived(id, { property_id: doc.property_id })
}

export async function getComplianceDocuments(
  propertyId: string,
  includeHistory: boolean = false
): Promise<ComplianceDocument[]> {
  await verifyPropertyAccess(propertyId)

  let query = supabase
    .from('compliance_documents')
    .select('*')
    .eq('property_id', propertyId)
    .is('deleted_at', null)

  // Only filter by is_current if not including history
  // Use neq false instead of eq true to handle null/undefined values
  if (!includeHistory) {
    query = query.neq('is_current', false)
  }

  const { data, error } = await query.order('expiry_date', { ascending: true, nullsFirst: false })

  if (error) throw error
  return await addDocumentUrls(data as ComplianceDocument[])
}

export async function getComplianceHistory(
  propertyId: string
): Promise<ComplianceDocument[]> {
  await verifyPropertyAccess(propertyId)

  const { data, error } = await supabase
    .from('compliance_documents')
    .select('*')
    .eq('property_id', propertyId)
    .is('deleted_at', null)
    .eq('is_current', false)
    .order('created_at', { ascending: false })

  if (error) throw error
  return await addDocumentUrls(data as ComplianceDocument[])
}

export async function getComplianceDocument(id: string): Promise<ComplianceDocument | null> {
  const { data, error } = await supabase
    .from('compliance_documents')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }

  // Verify access via the document's property
  await verifyPropertyAccess(data.property_id)

  return await addDocumentUrl(data as ComplianceDocument)
}

export async function getAllComplianceDocuments(propertyIds?: string[], userId?: string): Promise<ComplianceDocument[]> {
  const resolvedUserId = userId ?? await getCurrentUserId()

  let query = supabase
    .from('compliance_documents')
    .select(`
      *,
      property:properties!inner(id, address_line1, city, postcode, owner_id)
    `)
    .eq('property.owner_id', resolvedUserId)
    .is('deleted_at', null)
    .neq('is_current', false)
    .order('expiry_date', { ascending: true, nullsFirst: false })

  query = applyPropertyFilter(query, propertyIds)

  const { data, error } = await query
  if (error) throw error
  return await addDocumentUrls(data as ComplianceDocument[])
}

export async function getExpiringDocuments(daysAhead: number = 90, userId?: string, propertyIds?: string[]): Promise<ComplianceDocument[]> {
  const resolvedUserId = userId ?? await getCurrentUserId()
  const futureDate = new Date()
  futureDate.setDate(futureDate.getDate() + daysAhead)

  let query = supabase
    .from('compliance_documents')
    .select(`
      *,
      property:properties!inner(id, address_line1, city, postcode, owner_id)
    `)
    .eq('property.owner_id', resolvedUserId)
    .is('deleted_at', null)
    .neq('is_current', false)
    .not('expiry_date', 'is', null)
    .lte('expiry_date', futureDate.toISOString().split('T')[0])
    .order('expiry_date', { ascending: true })

  query = applyPropertyFilter(query, propertyIds)

  const { data, error } = await query
  if (error) throw error
  return await addDocumentUrls(data as ComplianceDocument[])
}

export async function getExpiringDocCountsByProperty(): Promise<Record<string, number>> {
  const userId = await getCurrentUserId()
  const today = new Date().toISOString().split('T')[0]
  const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  const { data, error } = await supabase
    .from('compliance_documents')
    .select('property_id, expiry_date, property:properties!inner(owner_id)')
    .eq('property.owner_id', userId)
    .is('deleted_at', null)
    .neq('is_current', false)
    .not('expiry_date', 'is', null)
    .lte('expiry_date', thirtyDaysFromNow)

  if (error) throw error

  const counts: Record<string, { expired: number; expiring: number }> = {}
  for (const doc of data || []) {
    if (!counts[doc.property_id]) {
      counts[doc.property_id] = { expired: 0, expiring: 0 }
    }
    if (doc.expiry_date <= today) {
      counts[doc.property_id].expired++
    } else {
      counts[doc.property_id].expiring++
    }
  }

  // Return combined counts with expired taking priority for display
  const result: Record<string, number> = {}
  for (const [propertyId, { expired, expiring }] of Object.entries(counts)) {
    result[propertyId] = expired + expiring
  }
  return result
}

export async function getExpiringDocDetailsByProperty(): Promise<Record<string, { expired: number; expiring: number }>> {
  const userId = await getCurrentUserId()
  const today = new Date().toISOString().split('T')[0]
  const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  const { data, error } = await supabase
    .from('compliance_documents')
    .select('property_id, expiry_date, property:properties!inner(owner_id)')
    .eq('property.owner_id', userId)
    .is('deleted_at', null)
    .neq('is_current', false)
    .not('expiry_date', 'is', null)
    .lte('expiry_date', thirtyDaysFromNow)

  if (error) throw error

  const counts: Record<string, { expired: number; expiring: number }> = {}
  for (const doc of data || []) {
    if (!counts[doc.property_id]) {
      counts[doc.property_id] = { expired: 0, expiring: 0 }
    }
    if (doc.expiry_date <= today) {
      counts[doc.property_id].expired++
    } else {
      counts[doc.property_id].expiring++
    }
  }
  return counts
}

// Get count of missing required documents per property
export async function getMissingRequiredDocsByProperty(): Promise<Record<string, number>> {
  const userId = await getCurrentUserId()

  // Fetch properties with their types — scoped to current user
  const { data: properties, error: propError } = await supabase
    .from('properties')
    .select('id, property_type')
    .eq('owner_id', userId)
    .is('deleted_at', null)

  if (propError) throw propError

  // Fetch current compliance documents — scoped to current user's properties
  const propertyIds = (properties || []).map(p => p.id)
  if (propertyIds.length === 0) return {}

  const { data: docs, error: docsError } = await supabase
    .from('compliance_documents')
    .select('property_id, document_type')
    .in('property_id', propertyIds)
    .is('deleted_at', null)
    .neq('is_current', false)

  if (docsError) throw docsError

  // Required docs based on property type
  const getRequiredDocTypes = (propertyType: string): string[] => {
    const baseRequired = ['gas_safety', 'eicr', 'epc']
    if (propertyType === 'hmo') {
      return [...baseRequired, 'hmo_licence', 'fire_safety', 'legionella']
    }
    if (propertyType === 'commercial') {
      return ['fire_safety', 'eicr']
    }
    return baseRequired
  }

  const counts: Record<string, number> = {}
  for (const property of properties || []) {
    const requiredTypes = getRequiredDocTypes(property.property_type)
    const propertyDocs = (docs || []).filter(d => d.property_id === property.id)
    const missingCount = requiredTypes.filter(reqType =>
      !propertyDocs.some(d => d.document_type === reqType)
    ).length
    if (missingCount > 0) {
      counts[property.id] = missingCount
    }
  }
  return counts
}

async function addDocumentUrl(doc: ComplianceDocument): Promise<ComplianceDocument> {
  if (doc.storage_path) {
    return {
      ...doc,
      url: await getSignedUrl('compliance-documents', doc.storage_path),
    }
  }
  return doc
}

export async function addDocumentUrls(docs: ComplianceDocument[]): Promise<ComplianceDocument[]> {
  const docsWithPaths = docs.filter(d => d.storage_path)
  if (docsWithPaths.length === 0) return docs

  const paths = docsWithPaths.map(d => d.storage_path!)
  const urls = await getSignedUrls('compliance-documents', paths)

  const pathToUrl = new Map(paths.map((path, i) => [path, urls[i]]))
  return docs.map(doc =>
    doc.storage_path ? { ...doc, url: pathToUrl.get(doc.storage_path) || '' } : doc
  )
}
